import { SecureLoader } from "@ews-admin/global-design-system";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { AdminDocumentationViewer } from "../../pages/AdminDocumentationViewer";
import { AdminProfile } from "../../pages/AdminProfile";
import { AdminSettings } from "../../pages/AdminSettings";
import { AdminAnalytics } from "../../pages/components/AdminAnalytics";
import { UserGovernance } from "../../pages/components/UserGovernance";
import { FacilityManagement } from "../../pages/components/FacilityManagement";
import { LicencesManagement } from "../../pages/components/LicencesManagement";
import { LicencePromotionsManagement } from "../../pages/components/LicencePromotionsManagement";
import { FaqManagement } from "../../pages/components/FaqManagement";
import { FeatureManagement } from "../../pages/components/FeatureManagement";
import { InvoiceManagement } from "../../pages/components/InvoiceManagement";
import { PricingManagement } from "../../pages/components/PricingManagement";
import { DoctorsManagement } from "../../pages/components/DoctorsManagement";
import { ManagerManagement } from "../../pages/components/ManagerManagement";
import { CareTypeManagement } from "../../pages/components/CareTypeManagement";
import { SpecialtyManagement } from "../../pages/components/SpecialtyManagement";
import { UsersManagement } from "../../pages/components/UsersManagement";
import { AdminDashboard } from "../../pages/Dashboard";
import { DoctorDetailPage } from "../../pages/DoctorDetailPage";
import { FacilityDetailPage } from "../../pages/FacilityDetailPage";
import { AnnouncementConfig } from "../../pages/governance/AnnouncementConfig";
import { FeatureFlagConfig } from "../../pages/governance/FeatureFlagConfig";
import { MaintenanceConfig } from "../../pages/governance/MaintenanceConfig";
import { ManagerDetailPage } from "../../pages/ManagerDetailPage";
import OnboardingPage from "../../pages/OnboardingPage";
import { selectSessionAuthState } from "../../store/reducers/sessionSlice";
import { getAdminUrl, getLoginApplicationUrl } from "../../utils/config-utils";
import { DashboardLayout } from "../layout/DashboardLayout";
import { UnauthorizedAccess } from "./UnauthorizedAccess";

export const AdminRouteWrapper: React.FC = () => {
  const { t } = useTranslation();
  const { loading, session } = useSelector(selectSessionAuthState);

  // MED-1543: a visitor who is not signed in has no role to be refused for.
  // They used to land on "Accès refusé", which told them their account had the
  // role "Inconnu" and then sent them to the patient app — so the one thing
  // they needed, the sign-in page, was the one place they were not offered.
  // Same destination PrivateRoute sends an invalid session to.
  const isSignedIn = !loading && !!session?.user;
  const isSignedOut = !loading && !session?.user;

  useEffect(() => {
    if (isSignedOut) {
      window.location.href = getLoginApplicationUrl(getAdminUrl());
    }
  }, [isSignedOut]);

  // Show loading while session is being validated, and while the redirect
  // above is on its way.
  if (loading || isSignedOut) {
    return <SecureLoader label={t("common.loading")} />;
  }
  // Check if user has admin role
  // Supports multiple admin role variations: "admin", "super_admin", "superadmin"
  const userRole = session?.user?.roleName?.toLowerCase();
  const isAdmin =
    userRole === "admin" ||
    userRole === "super_admin" ||
    userRole === "superadmin";

  // Signed in, but as someone who does not belong here — a patient or a
  // doctor who followed an admin link. That is what this screen is for.
  if (isSignedIn && !isAdmin) {
    return (
      <UnauthorizedAccess
        userRole={session?.user?.roleName}
        requiredRole="admin"
      />
    );
  }

  // If user is admin, render the layout with routes
  return (
    <DashboardLayout>
      <Routes>
        <Route path="/" element={<AdminDashboard />} />
        {/* MED-1448 / MED-1449: the header user menu's own admin routes. */}
        <Route path="/profile" element={<AdminProfile />} />
        <Route path="/settings" element={<AdminSettings />} />
        <Route path="/facilities" element={<FacilityManagement />} />
        <Route
          path="/facilities/:facilityId"
          element={<FacilityDetailPage />}
        />
        <Route path="/managers" element={<ManagerManagement />} />
        <Route path="/managers/:managerId" element={<ManagerDetailPage />} />
        <Route path="/doctors" element={<DoctorsManagement />} />
        <Route path="/users" element={<UsersManagement />} />
        <Route path="/invoices" element={<InvoiceManagement />} />
        <Route path="/features" element={<FeatureManagement />} />
        <Route path="/docs/:docId" element={<AdminDocumentationViewer />} />
        <Route path="/user-governance" element={<UserGovernance />} />
        <Route
          path="/governance/announcement"
          element={<AnnouncementConfig />}
        />
        <Route path="/governance/maintenance" element={<MaintenanceConfig />} />
        <Route
          path="/governance/feature-flags"
          element={<FeatureFlagConfig />}
        />
        <Route path="/analytics" element={<AdminAnalytics />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/faq" element={<FaqManagement />} />
        <Route path="/specialties" element={<SpecialtyManagement />} />
        <Route path="/care-types" element={<CareTypeManagement />} />
        <Route path="/pricing" element={<PricingManagement />} />
        <Route path="/licences" element={<LicencesManagement />} />
        <Route
          path="/licence-promotions"
          element={<LicencePromotionsManagement />}
        />
        <Route path="/doctors/:doctorId" element={<DoctorDetailPage />} />
        <Route path="*" element={<Navigate to="/admin" />} />
      </Routes>
    </DashboardLayout>
  );
};
